/**
 * SAM-1298 — the detail of a business request (canvas C7 extended, S2–S7): the request's block, who sent it and when,
 * and the Request status selector (Approved / Denied, a note required to deny). An edit shows only what changes
 * (before → after); an Estate request shows the estate group it would administer. An Employee request is a Team v3
 * carnet (person + assignment); the tenant Front Desk answers those only.
 * New file under the BaseUI freeze (SAM-175): Tailwind + tokens.
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ChevronDown, Loader2 } from 'lucide-react';
import { AccessDenied } from '../../components/AccessDenied';
import { toaster } from '../../components/ui/toast';
import { StatusChip } from '../BusinessPage/ActivityControls';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useBusinessPositions } from '../../hooks/useBusinessEmployees';
import { businessRequestsApi, type RequestUiStatus } from '../../api/businessRequestsApi';
import {
  TYPE_LABEL, businessRequestKeys, canDecide, changedFields, dayOf, decisionErrorText, decisionHint, detailFields, groupNamer,
  isOpen, statusView, visibleTypes,
} from './businessRequestsLogic';

const card = 'bg-white border border-solid border-[var(--line)] rounded-[12px] px-5 sm:px-6 py-5';

export const Block: React.FC<{ title: string; rows: Array<{ label: string; value: React.ReactNode }>; children?: React.ReactNode }> = ({ title, rows, children }) => (
  <section className={card}>
    <h2 className="m-0 mb-2 text-[18px] font-bold text-[var(--ink-900)]">{title}</h2>
    {children}
    {rows.map((r, i) => (
      <div key={r.label} className={`flex justify-between gap-4 py-[11px] text-[15px] ${i ? 'border-solid border-0 border-t border-[#eee]' : ''}`}>
        <span className="text-[var(--ink-500)] shrink-0">{r.label}</span>
        <span className="font-medium text-right text-[var(--ink-900)] min-w-0 break-words">{r.value}</span>
      </div>
    ))}
  </section>
);

type Decision = 'approve' | 'reject';

/** The Request status panel of C7 (same look as the Estate Requests selector). */
const StatusPanel: React.FC<{
  status: RequestUiStatus;
  /** Still waiting for the tenant (stored status `sent`). */
  pending: boolean;
  canChange: boolean;
  hint: string;
  note: string | null;
  saving: boolean;
  error: string | null;
  onSubmit: (decision: Decision, note: string) => void;
  onCancel: () => void;
}> = ({ status, pending, canChange, hint, note, saving, error, onSubmit, onCancel }) => {
  const [decision, setDecision] = useState<Decision>('approve');
  const [text, setText] = useState('');
  const open = pending && canChange;
  const view = statusView(status);
  return (
    <div className="flex flex-col gap-3 p-4 bg-[#f8f9fa] rounded-[12px] border border-solid border-[#e1e5e9]">
      <div className="flex items-center justify-between">
        <span className="text-[16px] font-semibold text-[var(--ink-900)]">Request status</span>
        <StatusChip tone={view.tone} label={view.label} />
      </div>
      {open && (
        <>
          <label className="relative flex h-[52px] items-center justify-between rounded-[8px] border border-solid border-[#e2e2e2] bg-[#f9f9f9] px-4 text-[16px] text-[var(--ink-900)]">
            <span>{decision === 'approve' ? 'Approved' : 'Denied'}</span>
            <ChevronDown size={20} className="text-[var(--ink-500)]" aria-hidden />
            <select
              aria-label="Request status"
              value={decision}
              onChange={(e) => setDecision(e.target.value as Decision)}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            >
              <option value="approve">Approved</option>
              <option value="reject">Denied</option>
            </select>
          </label>
          {decision === 'reject' && (
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-[var(--ink-900)]">
              Note for the business (required)
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                className="rounded-[8px] border border-solid border-[#e2e2e2] bg-white p-3 text-[15px] font-normal font-[inherit] text-[var(--ink-900)]"
              />
            </label>
          )}
          {error && <div role="alert" className="text-[13px] text-[var(--danger,#b42318)]">{error}</div>}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              disabled={saving}
              className="h-11 px-5 rounded-[8px] border border-solid border-[#e2e2e2] bg-white text-[15px] font-medium font-[inherit] cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSubmit(decision, text.trim())}
              disabled={saving || (decision === 'reject' && !text.trim())}
              className="h-11 px-[22px] rounded-[8px] border-0 bg-[var(--tenant-primary,#005488)] text-white text-[15px] font-semibold font-[inherit] cursor-pointer inline-flex items-center gap-2 disabled:opacity-60"
            >
              {saving && <Loader2 size={16} className="animate-spin" aria-hidden />}
              Update status
            </button>
          </div>
          <span className="text-[13px] text-[var(--ink-500)]">
            {decision === 'reject' ? 'Denied sends this note to the business. They can edit and send it again.' : hint}
          </span>
        </>
      )}
      {status === 'denied' && note && <span className="text-[14px] text-[var(--ink-700)]">{note}</span>}
    </div>
  );
};

const DetailLayout: React.FC<{ title: string; onBack: () => void; main: React.ReactNode; side: React.ReactNode }> = ({ title, onBack, main, side }) => (
  <div className="h-full overflow-auto bg-white">
    <div className="px-4 md:px-6 py-5 flex flex-col gap-6 max-w-[1280px]">
      <div className="flex items-center gap-3.5">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to Business Requests"
          className="w-11 h-11 shrink-0 rounded-[10px] border border-solid border-[#e2e2e2] bg-white flex items-center justify-center cursor-pointer"
        >
          <ArrowLeft size={20} aria-hidden />
        </button>
        <h1 className="m-0 text-[24px] md:text-[30px] font-bold text-[var(--ink-900)]">{title}</h1>
      </div>
      <div className="flex flex-col lg:flex-row gap-5 items-stretch lg:items-start">
        <div className="flex flex-col gap-4 flex-1 min-w-0">{main}</div>
        <div className="flex flex-col gap-4 w-full lg:w-[460px] shrink-0">{side}</div>
      </div>
    </div>
  </div>
);

const LIST = '/support/business-requests';

export const BusinessRequestDetail: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const permissions = useUserPermissions();
  const types = visibleTypes(permissions);
  const [error, setError] = useState<string | null>(null);
  const request = useQuery({
    queryKey: [...businessRequestKeys.list, id],
    queryFn: () => businessRequestsApi.get(id),
    enabled: !!id && types.length > 0,
  });
  const options = useQuery({
    queryKey: businessRequestKeys.options,
    queryFn: businessRequestsApi.options,
    enabled: permissions.isAdmin,
    staleTime: 5 * 60 * 1000,
  });
  // A carnet's position arrives as its key; the business's positions give the label (as in Team › Add).
  const positions = useBusinessPositions(request.data?.type === 'employee' ? request.data.business_id : null);
  const decide = useMutation({
    mutationFn: ({ decision, note }: { decision: Decision; note: string }) =>
      (decision === 'approve' ? businessRequestsApi.approve(id) : businessRequestsApi.reject(id, note)),
    onSuccess: (_d, v) => {
      setError(null);
      toaster.positive(v.decision === 'approve' ? 'Approved' : 'Denied · the business sees your note', { autoHideDuration: 4000 });
      void qc.invalidateQueries({ queryKey: businessRequestKeys.list });
    },
    onError: (e) => setError(decisionErrorText(e)),
  });

  if (permissions.isLoading) return <div className="p-6 text-sm text-[var(--ink-500)]">Loading…</div>;
  if (!types.length) return <AccessDenied />;
  const back = () => navigate(LIST);
  if (request.isLoading) return <div className="p-6 text-sm text-[var(--ink-500)]">Loading…</div>;
  const r = request.data;
  if (!r) return <div role="alert" className="p-6 text-sm text-[var(--ink-500)]">This request could not be loaded.</div>;
  // The tenant Front Desk answers carnets only (Employee); the server already hides the rest from it.
  if (!types.includes(r.type)) return <AccessDenied />;

  const groupName = groupNamer(options.data)(r.estate_group_id);
  const category = options.data?.categories.find((c) => c.id === r.payload?.business_category_id);
  const title = r.type === 'employee' ? 'Carnet request' : r.kind === 'edit' ? `${TYPE_LABEL[r.type]} edit` : `${TYPE_LABEL[r.type]} request`;
  const positionKey = r.payload?.position_key;
  const position = positions.data?.find((pos) => pos.key === positionKey)?.label_en;
  const fields = (r.kind === 'edit' ? [] : detailFields(r.type, r.payload))
    .map((f) => (f.label === 'Position' && position ? { ...f, value: position } : f));
  const main = (
    <>
      {r.kind === 'edit' && (
        <Block
          title="Changes"
          rows={changedFields(r).map((c) => ({
            label: c.label,
            value: <><s className="text-[var(--ink-500)] font-normal">{c.before || '—'}</s>{'  '}{c.after || '—'}</>,
          }))}
        />
      )}
      {r.type === 'employee' ? (
        <>
          <Block title="Person" rows={[{ label: 'Name', value: r.title || '—' }, ...fields.filter((f) => f.label !== 'Position' && f.label !== 'Valid until')]} />
          <Block
            title="Carnet"
            rows={[
              { label: 'Business', value: r.business_name || '—' },
              ...fields.filter((f) => f.label === 'Position' || f.label === 'Valid until'),
            ]}
          />
        </>
      ) : (
        <Block
          title={TYPE_LABEL[r.type]}
          rows={[
            ...(r.type !== 'business' && r.business_name ? [{ label: 'Business', value: r.business_name }] : []),
            ...(r.kind === 'edit' && r.title ? [{ label: 'Name', value: r.title }] : []),
            ...fields,
            ...(r.type === 'business' && category ? [{ label: 'Category', value: category.name_en || category.name_es || '' }] : []),
            ...(r.type === 'business' && !r.is_estate && groupName ? [{ label: 'Estate group', value: groupName }] : []),
          ]}
        />
      )}
      {r.is_estate && (
        <Block title="Estate group" rows={[{ label: 'Administers', value: groupName || '—' }]}>
          <p className="m-0 mb-1 text-[13px] text-[var(--ink-500)]">Only the tenant Admin or Xhinola can approve it.</p>
        </Block>
      )}
    </>
  );
  const side = (
    <>
      <StatusPanel
        status={r.ui_status}
        pending={isOpen(r)}
        canChange={canDecide(permissions, r.type)}
        hint={decisionHint(r, groupName)}
        note={r.note}
        saving={decide.isPending}
        error={error}
        onSubmit={(decision, note) => decide.mutate({ decision, note })}
        onCancel={back}
      />
      <Block
        title="Requested by"
        rows={[
          { label: 'Name', value: r.submitted_by?.name || r.submitted_by?.email || '—' },
          ...(r.submitted_by?.email && r.submitted_by?.name ? [{ label: 'Email', value: r.submitted_by.email }] : []),
          ...(r.type === 'business' && r.kind === 'new' ? [{ label: 'Role', value: 'Owner (new account)' }] : []),
          { label: 'Sent', value: dayOf(r.sent_at) || '—' },
        ]}
      />
    </>
  );
  return <DetailLayout title={title} onBack={back} main={main} side={side} />;
};

export default BusinessRequestDetail;
